
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/components/auth/AuthProvider";
import { Profile, NotificationPreferences } from "@/integrations/supabase/types/subscription";
import { Json } from "@/integrations/supabase/types";

export function useMembershipStatus() {
  const { user } = useAuth();

  const { data: profile, isLoading: isProfileLoading } = useQuery({
    queryKey: ['profile', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      
      const { data, error } = await supabase
        .from('profiles')
        .select(`
          *,
          subscription_plans (
            id,
            name,
            description,
            price_id,
            price_amount,
            currency,
            features,
            created_at
          )
        `)
        .eq('id', user.id)
        .single();

      if (error) throw error;
      
      let parsedNotificationPreferences: NotificationPreferences;
      
      if (typeof data.notification_preferences === 'string') {
        parsedNotificationPreferences = JSON.parse(data.notification_preferences);
      } else if (!data.notification_preferences) {
        parsedNotificationPreferences = {
          email: true,
          push: true
        };
      } else {
        const prefs = data.notification_preferences as Record<string, unknown>;
        parsedNotificationPreferences = {
          email: typeof prefs.email === 'boolean' ? prefs.email : true,
          push: typeof prefs.push === 'boolean' ? prefs.push : true,
          is_admin: typeof prefs.is_admin === 'boolean' ? prefs.is_admin : false
        };
      }

      return {
        ...data,
        notification_preferences: parsedNotificationPreferences
      } as Profile;
    },
    enabled: !!user?.id
  });

  const { data: tokenAllocation, isLoading: isTokensLoading } = useQuery({
    queryKey: ['tokenAllocation', user?.id],
    queryFn: async () => {
      // If user is admin, don't fetch token allocation
      if (profile?.notification_preferences?.is_admin) {
        return {
          monthly_tokens: Number.MAX_SAFE_INTEGER,
          tokens_used: 0
        };
      }

      if (!user?.id) return null;

      const { data, error } = await supabase
        .from('token_allocations')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!user?.id && !profile?.notification_preferences?.is_admin
  });

  const getMembershipLevel = () => {
    if (profile?.notification_preferences?.is_admin) return "admin";
    if (!profile?.subscription_plans?.name) return "free";
    
    const planName = profile.subscription_plans.name.toLowerCase();
    if (planName.includes("pro")) return "pro";
    if (planName.includes("basic")) return "basic";
    return "free";
  };

  const hasEnoughTokens = (requiredTokens: number) => {
    // Admin users always have enough tokens
    if (profile?.notification_preferences?.is_admin) return true;
    if (!tokenAllocation) return false;
    return (tokenAllocation.monthly_tokens - tokenAllocation.tokens_used) >= requiredTokens;
  };

  const canUseFeature = (requiredLevel: "basic" | "pro") => {
    // Admin users can use all features
    if (profile?.notification_preferences?.is_admin) return true;
    
    const currentLevel = getMembershipLevel();
    if (requiredLevel === "basic") {
      return currentLevel === "basic" || currentLevel === "pro";
    }
    return currentLevel === "pro";
  };

  return {
    isLoading: isProfileLoading,
    membershipLevel: getMembershipLevel(),
    hasEnoughTokens,
    canUseFeature,
    tokenAllocation,
    profile
  };
}
